import { forwardRef } from 'react';
import { useScrollStreamFeedBottom } from '../../hooks/useScrollStreamFeedBottom';
import FeedCardSkeleton from './FeedCardSkeleton';
import FeedInspectProvider from '../FeedInspectProvider';
import EmailDiscovery from '../../modules/Email/EmailDiscovery';
import PhoneIntelligence from '../../modules/Phone/PhoneIntelligence';
import PersonDiscovery from '../../modules/Person/PersonDiscovery';
import CorporateDueDiligence from '../../modules/Corporate/CorporateDueDiligence';
import { TAB_COMPANY, TAB_EMAIL, TAB_PERSON, TAB_PHONE } from '../../utils/investigationWorkspaceUtils';

const InvestigationResultsPanel = forwardRef(function InvestigationResultsPanel(
  {
    activeTab,
    displayResults = [],
    loading = false,
    getDisplayName,
    personCategoryFilter,
    onPersonCategoryFilter,
    onEmailAnalysis,
    onPivotToPerson,
    onPivotLead,
    currentData = {},
    statsBar = null,
    exiting = false,
  },
  ref,
) {
  const rows = Array.isArray(displayResults) ? displayResults : [];
  const data = currentData && typeof currentData === 'object' ? currentData : {};
  const discoveryFeedEndRef = useScrollStreamFeedBottom({
    count: rows.length,
    // Never auto-scroll on each SSE row — keeps the user's viewport stable while streaming.
    enabled: false,
  });

  return (
    <FeedInspectProvider
      discoveryPivots={
        Array.isArray(data.extractedLeads?.pivots) ? data.extractedLeads.pivots : []
      }
      onPivotLead={onPivotLead}
    >
      <section
        ref={ref}
        className={`home-results-panel home-results-feed-anchor mt-3 min-w-0 max-w-full overflow-x-clip pb-28 sm:pb-24 ${
          exiting
            ? 'opacity-0 transition-opacity duration-150 ease-out pointer-events-none'
            : 'animate-in fade-in duration-500'
        }`}
        aria-label="Investigation results"
        aria-hidden={exiting || undefined}
        data-feed-safe-area="mobile-hud"
        data-exiting={exiting ? 'true' : undefined}
      >
        {statsBar}

        <h3 className="mb-3 font-mono text-[11px] font-black uppercase tracking-[0.14em] text-cyan-300 sm:mb-4 sm:text-xs sm:tracking-[0.16em] md:text-sm">
          Intelligence Discovery Feed
        </h3>

        <div className="min-w-0 max-w-full space-y-2.5 overflow-x-clip scroll-smooth pb-4 text-sm sm:text-[15px]">
          {rows.length === 0 &&
          loading &&
          activeTab !== TAB_PERSON &&
          activeTab !== TAB_EMAIL &&
          activeTab !== TAB_PHONE ? (
            <FeedCardSkeleton count={4} />
          ) : null}
          {activeTab === TAB_EMAIL ? (
            <EmailDiscovery
              displayResults={rows}
              loading={loading}
              getDisplayName={getDisplayName}
              onPivotToPerson={onPivotToPerson}
            />
          ) : null}
          {activeTab === TAB_PHONE ? (
            <PhoneIntelligence
              displayResults={rows}
              loading={loading}
              getDisplayName={getDisplayName}
              extractedLeads={data.extractedLeads}
              onEmailAnalysis={onEmailAnalysis}
              onPivotToPerson={onPivotToPerson}
              onPivotLead={onPivotLead}
            />
          ) : null}
          {activeTab === TAB_PERSON ? (
            <PersonDiscovery
              displayResults={rows}
              loading={loading}
              getDisplayName={getDisplayName}
              activeFilter={personCategoryFilter}
              onFilterChange={onPersonCategoryFilter}
              extractedLeads={data.extractedLeads}
            />
          ) : null}
          {activeTab === TAB_COMPANY ? (
            <CorporateDueDiligence
              displayResults={rows}
              loading={loading}
              getDisplayName={getDisplayName}
              scanComplete={Boolean(data?.completed)}
              companyName={data?.name || ''}
              companyDomain={data?.domain || ''}
              locationTags={Array.isArray(data?.locationTags) ? data.locationTags : []}
              onPivotToPerson={onPivotToPerson}
            />
          ) : null}
          <span ref={discoveryFeedEndRef} className="block h-px w-full shrink-0 scroll-mt-4" aria-hidden />
        </div>
      </section>
    </FeedInspectProvider>
  );
});

export default InvestigationResultsPanel;
